import { AxiosError } from 'axios'
import { ZodError } from 'zod'

import { ApiClient } from '@src/ApiClient'

import { getGenericRequestErrorMessage, handleCommonErrors } from '../../../modules/Shared/utils/handleCommonErrors'
import {
  RobotType,
  RobotTypeSchema,
  RobotTypesSchema,
  RobotTypeStatus,
  SaveRobotType,
} from '../../RobotTypes/domain/RobotType'
import { InstallationMetricsFiltersType } from '../application/getInstallationMetrics'
import { CreateInstallation } from '../domain/CreateInstallation'
import { Installation, InstallationSchema } from '../domain/Installation'
import { InstallationMetricsPagination } from '../domain/InstallationMetricsPagination'
import { InstallationPagination } from '../domain/InstallationPagination'
import { InstallationRepository } from '../domain/InstallationRepository'

const apiClient = ApiClient()

async function findAllRobotTypes(status?: RobotTypeStatus) {
  return await apiClient
    .get<Array<RobotType>>('/installations/robot-types/', { params: { status } })
    .then(({ data }) => RobotTypesSchema.parse(data))
    .catch((error: AxiosError | ZodError) => {
      throw handleCommonErrors(error)
    })
}

async function saveRobotType(robotType: SaveRobotType) {
  return await apiClient
    .post<RobotType>('/installations/robot-types/', robotType)
    .then(({ data }) => RobotTypeSchema.parse(data))
    .catch((error: AxiosError | ZodError) => {
      throw handleCommonErrors(error, getGenericRequestErrorMessage())
    })
}

async function updateRobotType(robotTypeId: string, robotType: SaveRobotType) {
  return await apiClient
    .patch<RobotType>(`/installations/robot-types/${robotTypeId}`, robotType)
    .then(({ data }) => RobotTypeSchema.parse(data))
    .catch((error: AxiosError | ZodError) => {
      throw handleCommonErrors(error, getGenericRequestErrorMessage())
    })
}

async function deleteRobotType(robotTypeId: string) {
  return await apiClient
    .delete(`/installations/robot-types/${robotTypeId}`)
    .then(() => null)
    .catch((error: AxiosError) => {
      throw handleCommonErrors(error)
    })
}

async function findAll(filters: InstallationsPaginationQueryAndFilters) {
  const { page, size, searchText, status, installationTypeList } = filters
  const installation = await apiClient
    .get<InstallationPagination>('/installations/', {
      params: {
        page,
        size,
        searchText,
        status,
        ...(installationTypeList?.length ? { installationTypeList } : {}),
      },
      paramsSerializer: {
        indexes: null,
      },
    })
    .then(({ data }) => data)
    .catch((error: AxiosError) => {
      throw handleCommonErrors(error)
    })
  return installation
}

async function findById(id: string) {
  const installationById = await apiClient
    .get<Installation>(`/installations/${id}`)
    .then(({ data }) => data)
    .catch((error: AxiosError) => {
      throw handleCommonErrors(error)
    })
  return installationById
}

async function save(installation: CreateInstallation) {
  return await apiClient
    .post<Installation>('/installations/', installation)
    .then(({ data }) => {
      return InstallationSchema.parse(data)
    })
    .catch((error: AxiosError | ZodError) => {
      throw handleCommonErrors(error, getGenericRequestErrorMessage())
    })
}

async function update(installation: Installation) {
  const installationId = installation.installationId
  return await apiClient
    .patch<Installation>(`/orc/installations/${installationId}`, installation)
    .then(({ data }) => InstallationSchema.parse(data))
    .catch((error: AxiosError | ZodError) => {
      throw handleCommonErrors(error)
    })
}

async function updateStatus(installationId: string, status: 'ACTIVE' | 'NOT_ACTIVE') {
  return await apiClient
    .patch<Installation>(`/orc/installations/status`, { installationId, status })
    .then(() => null)
    .catch((error: AxiosError | ZodError) => {
      throw handleCommonErrors(error)
    })
}

async function deleteInstallation(installationId: string) {
  return await apiClient
    .patch<Installation>(`/orc/installations/${installationId}/delete`)
    .then(() => null)
    .catch((error: AxiosError | ZodError) => {
      throw handleCommonErrors(error)
    })
}

async function getInstallationMetrics(filters: InstallationMetricsFiltersType) {
  const { installationStatus, installationTypeList, ...rest } = filters
  const params = {
    ...rest,
    ...(installationStatus === 'ALL' ? {} : { installationStatus }),
    ...(installationTypeList?.length ? { installationTypeList } : {}),
  }

  return await apiClient
    .get<InstallationMetricsPagination>('/installations/metrics/patients', {
      params,
      paramsSerializer: {
        indexes: null,
      },
    })
    .then(({ data }) => data)
    .catch((error: AxiosError) => {
      throw handleCommonErrors(error)
    })
}

export function ApiInstallationRepository(): InstallationRepository {
  return {
    findAll,
    findById,
    save,
    update,
    updateStatus,
    deleteInstallation,
    getInstallationMetrics,
    findAllRobotTypes,
    saveRobotType,
    updateRobotType,
    deleteRobotType,
  }
}
